import React, { useEffect, useState } from 'react';
import { useAide } from './AideContexte';
import './Aide.css';

/**
 * LE VOLET D'AIDE — commun à tous les modules (étapes A1 → A3)
 * ============================================================
 * Maquette : doc/maquettes/aide_contextuelle_maquette.html (volet de droite)
 *
 * À droite, SOUS le bandeau du module (`ancre`) : l'écran reste visible, le module le décale de la largeur du volet
 * (classe `aide-ouverte` sur sa racine). Trois onglets :
 *   Cet onglet    la fiche de l'écran ouvert — elle SUIT la navigation ; sans fiche : « Aide pas encore écrite » ;
 *   Je veux…      les tâches courantes, avec une recherche ; chacune ouvre son écran ;
 *   Quoi de neuf  les changements visibles du module ; l'ouvrir les marque comme lus (pastille du « ? »).
 * En bas : « Proposer une amélioration » (v99 ; « Signaler un manque dans cette aide » avant).
 */

/** « **Envoyer** le lien » → Envoyer en gras. Le texte vient du fichier d'aide, jamais de l'utilisateur. */
export function Gras({ texte }) {
    return String(texte).split(/(\*\*[^*]+\*\*)/).map((m, i) => (
        m.startsWith('**') && m.endsWith('**') ? <b key={i}>{m.slice(2, -2)}</b> : <React.Fragment key={i}>{m}</React.Fragment>
    ));
}

const sansAccent = (s) => String(s).toLowerCase().normalize('NFD').replace(/[̀-ͯ]/g, '');
const dateFr = (iso) => { const [a, m, j] = String(iso).split('-'); return `${j}/${m}/${a}`; };

function FicheOnglet({ aide }) {
    const { donnees, ecran, ouvrir, raison } = aide;
    const fiche = donnees.FICHES[ecran];
    const groupe = ecran.split('/')[0];
    const libelleGroupe = donnees.GROUPES[groupe] || groupe;
    const infoEcran = donnees.ECRANS.find((e) => e.cle === ecran);
    const freres = donnees.ECRANS.filter((e) => e.cle === groupe || e.cle.startsWith(groupe + '/'));
    return (
        <>
            <div className="aide-v-fil">
                Vous êtes dans <b>{libelleGroupe}{infoEcran && freres.length > 1 ? ' › ' + infoEcran.libelle : ''}</b>
            </div>
            {fiche ? (
                <>
                    <div className="aide-v-titre">{fiche.titre}</div>
                    <div className="aide-v-sec"><i className="fa-solid fa-bullseye" /> À quoi sert cet écran</div>
                    <div className="aide-v-txt"><Gras texte={fiche.sert} /></div>
                    <div className="aide-v-sec"><i className="fa-solid fa-hand-pointer" /> Ce que vous pouvez faire ici</div>
                    <ul className="aide-v-ul">{fiche.gestes.map((g) => <li key={g}><Gras texte={g} /></li>)}</ul>
                    {fiche.reserve && <div className="aide-v-reserve"><i className="fa-solid fa-lock" /> <Gras texte={fiche.reserve} /></div>}
                    <div className="aide-v-sec"><i className="fa-solid fa-triangle-exclamation" /> À savoir</div>
                    <div className="aide-v-savoir">
                        <ul className="aide-v-ul">{fiche.savoir.map((s) => <li key={s}><Gras texte={s} /></li>)}</ul>
                    </div>
                </>
            ) : (
                <>
                    <div className="aide-v-titre">{infoEcran ? infoEcran.libelle : libelleGroupe}</div>
                    <div className="aide-v-vide">
                        <i className="fa-solid fa-pen-ruler" />
                        <b>Aide pas encore écrite pour cet écran</b>
                        {infoEcran ? infoEcran.court : ''}
                    </div>
                </>
            )}
            {freres.length > 1 && (
                <>
                    <div className="aide-v-sec"><i className="fa-solid fa-layer-group" /> Les écrans de « {libelleGroupe} »</div>
                    <div className="aide-v-autres">
                        {freres.map((e) => {
                            const ici = e.cle === ecran;
                            const pas = ici ? null : raison(e.cle);
                            const lien = !ici && !pas;
                            return (
                                <div key={e.cle} className={'aide-v-autre' + (ici ? ' ici' : '') + (pas ? ' ferme' : '')}
                                     role={lien ? 'button' : undefined} tabIndex={lien ? 0 : undefined}
                                     onClick={lien ? () => ouvrir(e.cle) : undefined}
                                     onKeyDown={lien ? (ev) => { if (ev.key === 'Enter') ouvrir(e.cle); } : undefined}>
                                    <i className={'fa-solid ' + e.icone} />
                                    <div>
                                        <div className="aide-v-n">{e.libelle}</div>
                                        <div className="aide-v-d">{e.court}{pas && <span className="aide-v-pas"> ({pas})</span>}</div>
                                    </div>
                                    {ici && <span className="aide-v-ici">vous êtes ici</span>}
                                </div>
                            );
                        })}
                    </div>
                </>
            )}
            {donnees.PROCESSUS && Object.keys(donnees.PROCESSUS).length > 0 && (
                <button type="button" className="aide-v-proc" onClick={() => aide.setProcessusOuvert(true)}>
                    <i className="fa-solid fa-diagram-project" /> Voir où cet écran se place dans le processus
                </button>
            )}
        </>
    );
}

function JeVeux({ aide }) {
    const { donnees, ouvrir, raison } = aide;
    const [q, setQ] = useState('');
    const [ouverte, setOuverte] = useState(null);
    const libelleEcran = (cle) => {
        const g = cle.split('/')[0];
        const e = donnees.ECRANS.find((x) => x.cle === cle);
        const grp = donnees.GROUPES[g] || g;
        return e && e.libelle !== grp ? `${grp} › ${e.libelle}` : grp;
    };
    const n = sansAccent(q.trim());
    const taches = donnees.TACHES.filter((t) => !n || sansAccent(`${t.question} ${t.groupe} ${t.gestes.join(' ')}`).includes(n));
    let groupe = null;
    return (
        <>
            <div className="aide-jv-search">
                <i className="fa-solid fa-magnifying-glass" />
                <input value={q} onChange={(e) => setQ(e.target.value)} placeholder={donnees.exemplesRecherche || 'Ex. relancer, tuteur, apprenti…'} aria-label="Rechercher une tâche" />
            </div>
            {taches.length === 0 && (
                <div className="aide-jv-rien">
                    Aucune tâche ne correspond.
                    <button type="button" className="aide-btn-sec" onClick={() => aide.setSignalerOuvert('aide')}><i className="fa-solid fa-flag" /> Signaler ce qui manque</button>
                </div>
            )}
            {taches.map((t) => {
                const titre = t.groupe !== groupe ? (groupe = t.groupe) : null;
                const ouv = ouverte === t.question;
                return (
                    <React.Fragment key={t.question}>
                        {titre && <div className="aide-jv-grp">{titre}</div>}
                        <div className={'aide-jv-t' + (ouv ? ' open' : '')}>
                            <button type="button" className="aide-jv-q" onClick={() => setOuverte(ouv ? null : t.question)} aria-expanded={ouv}>
                                <i className="fa-solid fa-chevron-right aide-jv-ch" />{t.question}
                            </button>
                            {ouv && (
                                <div className="aide-jv-r">
                                    <ol>{t.gestes.map((g) => <li key={g}><Gras texte={g} /></li>)}</ol>
                                    {raison(t.ecran)
                                        ? <div className="aide-v-pas"><i className="fa-solid fa-circle-info" /> {libelleEcran(t.ecran)} : {raison(t.ecran)}</div>
                                        : <button type="button" className="aide-btn-aller petit" onClick={() => ouvrir(t.ecran)}>
                                            <i className="fa-solid fa-arrow-right" /> Ouvrir {libelleEcran(t.ecran)}
                                        </button>}
                                </div>
                            )}
                        </div>
                    </React.Fragment>
                );
            })}
        </>
    );
}

function QuoiDeNeuf({ aide }) {
    const { donnees, lectures, marquerNeufLu, ouvrir, raison } = aide;
    // Ce qui était « nouveau » À L'OUVERTURE reste marqué tant que l'onglet est affiché, même une fois lu.
    const [luAvant] = useState(() => (lectures && lectures.neuf_lu_jusqu_au) || '');
    useEffect(() => { marquerNeufLu(); }, [marquerNeufLu]);
    return donnees.NEUF.map((x) => {
        const nouveau = x.date > luAvant;
        return (
            <div key={x.date + x.titre} className={'aide-qn' + (nouveau ? ' nouveau' : '')}>
                <div className="aide-qn-dt">{dateFr(x.date)}{nouveau && <span className="aide-qn-nv">nouveau</span>}</div>
                <div className="aide-qn-ti">{x.titre}</div>
                <div className="aide-qn-tx"><Gras texte={x.texte} /></div>
                {x.ecran && !raison(x.ecran) && <button type="button" className="aide-lien" onClick={() => ouvrir(x.ecran)}>Voir l'écran</button>}
            </div>
        );
    });
}

function VoletAide({ ancre }) {
    const aide = useAide();
    const ouvert = !!(aide && aide.ouvert);
    const [haut, setHaut] = useState(0);

    // Le volet commence sous le bandeau (collant) : sa position se relit à l'ouverture et au redimensionnement.
    useEffect(() => {
        if (!ouvert) return undefined;
        const mesurer = () => setHaut(ancre && ancre.current ? Math.max(0, Math.round(ancre.current.getBoundingClientRect().bottom)) : 0);
        mesurer();
        window.addEventListener('resize', mesurer);
        return () => window.removeEventListener('resize', mesurer);
    }, [ouvert, ancre]);

    if (!aide || !ouvert) return null;
    const { donnees, vue, setVue, setOuvert, nonLus } = aide;
    const onglets = [
        { cle: 'onglet', libelle: 'Cet onglet', icone: 'fa-book-open', present: true },
        { cle: 'jeveux', libelle: 'Je veux…', icone: 'fa-magnifying-glass', present: !!(donnees.TACHES && donnees.TACHES.length) },
        { cle: 'neuf', libelle: 'Quoi de neuf', icone: 'fa-gift', present: !!(donnees.NEUF && donnees.NEUF.length) }
    ].filter((o) => o.present);
    const vueSure = onglets.some((o) => o.cle === vue) ? vue : 'onglet';

    return (
        <aside className="aide-volet" style={{ top: haut }} aria-label="Aide">
            <div className="aide-v-head">
                <span className="aide-v-ic"><i className="fa-solid fa-circle-question" /></span>
                <div>
                    <div className="aide-v-tt">Aide</div>
                    <div className="aide-v-ss">{donnees.titreModule}</div>
                </div>
                <button type="button" className="aide-v-x" onClick={() => setOuvert(false)} title="Fermer (Échap)" aria-label="Fermer l'aide">
                    <i className="fa-solid fa-xmark" />
                </button>
            </div>
            {onglets.length > 1 && (
                <div className="aide-v-seg" role="tablist" aria-label="Aide">
                    {onglets.map((o) => (
                        <button key={o.cle} type="button" role="tab" aria-selected={vueSure === o.cle}
                                className={vueSure === o.cle ? 'on' : ''} onClick={() => setVue(o.cle)}>
                            <i className={'fa-solid ' + o.icone} /> {o.libelle}
                            {o.cle === 'neuf' && nonLus > 0 && <span className="aide-cnt">{nonLus}</span>}
                        </button>
                    ))}
                </div>
            )}
            {vueSure === 'onglet' && <div className="aide-v-suit"><i className="fa-solid fa-thumbtack" /> Le volet suit vos changements d'onglet.</div>}
            <div className="aide-v-body" data-ecran={aide.ecran} data-vue={vueSure}>
                {vueSure === 'onglet' && <FicheOnglet aide={aide} />}
                {vueSure === 'jeveux' && <JeVeux aide={aide} />}
                {vueSure === 'neuf' && <QuoiDeNeuf aide={aide} />}
            </div>
            <div className="aide-v-foot">
                <i className="fa-solid fa-lightbulb" />
                <button type="button" className="aide-lien" onClick={() => aide.setSignalerOuvert(true)}>Proposer une amélioration</button>
            </div>
        </aside>
    );
}

export default VoletAide;
